import 'regenerator-runtime/runtime';
import { call, delay, select, takeEvery } from 'redux-saga/effects';

import {
  Adresse,
  GlobalAction,
  GlobalActions,
  GlobalPut,
  isNeugeschaeft,
  ModalType,
  WrappedSavedStates,
  WrappedSavedStatesLoaded,
} from '@ps20-frontend/shop-integration-redux';
import { App, areAppTypesEquivalent } from '@ps20-frontend/frontend-models';
import { safeSaga } from '@ps20-frontend/safe-saga';
import { IAppState } from '../appState';
import { angebotIsTarifiert } from '../selectors/warenkorb.selector';

export function* requestStructuredRisikoortSaga() {
  yield takeEvery(
    GlobalActions.SAVED_STATES_LOADED,
    safeSaga(requestRisikoort, App.SHOP_DASHBOARD),
  );
}

export function* requestRisikoort(action: GlobalAction<WrappedSavedStatesLoaded>) {
  const state: IAppState = yield select();
  const angeboteTarifiertVWG = state.warenkorb.filter(
    angebot => areAppTypesEquivalent(angebot.appId, App.VWG) && angebotIsTarifiert(angebot),
  );
  const vwgWithoutStructuredRisikoort = angeboteTarifiertVWG.find(
    angebot => angebot.versicherterRisikoort == null,
  );

  if (vwgWithoutStructuredRisikoort) {
    const globalPut = GlobalPut.getInstance();
    yield call(globalPut.put, new GlobalActions().requestStructuredRisikoort(vwgWithoutStructuredRisikoort.appId));
  }

  const angeboteTarifiertVHV = state.warenkorb.filter(
    angebot => areAppTypesEquivalent(angebot.appId, App.VHV) && angebotIsTarifiert(angebot),
  );

  const vhvWithoutStructuredRisikoort = angeboteTarifiertVHV.find(
    angebot => angebot.versicherterRisikoort == null,
  );

  if (vhvWithoutStructuredRisikoort) {
    const globalPut = GlobalPut.getInstance();
    yield call(globalPut.put, new GlobalActions().requestStructuredRisikoort(vhvWithoutStructuredRisikoort.appId));
  }

  const savedStatesLoaded: WrappedSavedStates = action.payload.savedStatesLoaded;
  const vwgAdressesErsatzgeschaeft: Adresse[] = savedStatesLoaded
    .filter(app => areAppTypesEquivalent(app.appId, App.VWG) && !isNeugeschaeft(app.state.vertragsoptionen))
    .filter(vwgApp =>
      angeboteTarifiertVWG.find(angebot => angebot.angebotsId === vwgApp.angebotsId),
    )
    .map(vwgApp => vwgApp.state.risikoort);
  const vhvAdressesErsatzgeschaeft: Adresse[] = savedStatesLoaded
    .filter(app => areAppTypesEquivalent(app.appId, App.VHV) && !isNeugeschaeft(app.state.vertragsoptionen))
    .filter(vhvApp =>
      angeboteTarifiertVHV.find(angebot => angebot.angebotsId === vhvApp.angebotsId),
    )
    .map(vhvApp => vhvApp.state.risikoort);
  const statesWithDuplicateRisikoortExist: boolean =
    duplicateAdressesExist(vwgAdressesErsatzgeschaeft) ||
    duplicateAdressesExist(vhvAdressesErsatzgeschaeft);

  if (statesWithDuplicateRisikoortExist) {
    yield call(GlobalPut.getInstance().put, new GlobalActions().updateVorvertraegeFailed());
    yield delay(0);
    yield call(
      GlobalPut.getInstance().put,
      new GlobalActions().requestModal(ModalType.VORVERTRAEGE_STATUS_CHANGED),
    );
  }
}

function isDuplicateAdresse(risikoort1: Adresse, duplicates: Adresse[]): boolean {
  return duplicates.some(
    risikoort2 =>
      risikoort1.plz === risikoort2.plz &&
      risikoort1.ort === risikoort2.ort &&
      risikoort1.strasse === risikoort2.strasse &&
      risikoort1.hausnummer === risikoort2.hausnummer &&
      risikoort1.hausnrzus === risikoort2.hausnrzus,
  );
}

export function duplicateAdressesExist(adresses: Adresse[]): boolean {
  const unique: Adresse[] = [];

  for (const entry of adresses) {
    if (!isDuplicateAdresse(entry, unique)) {
      unique.push(entry);
    }
  }

  return unique.length < adresses.length;
}
